
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Card } from "../ui/Card";
import { 
  Shield, 
  Globe, 
  Mail, 
  Layers, 
  Zap, 
  Lock, 
  Database, 
  Terminal,
  ChevronRight,
  EyeOff,
  Code2
} from "lucide-react";
import { GhostBrandIcon } from "../ui/PartnerIcons";
import { Button } from "../ui/Button";

const ValueCard = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
    <div className="space-y-3 p-2 group">
        <div className="w-12 h-12 rounded-card bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-900 dark:text-white shadow-sm border border-stone-200 dark:border-white/10 group-hover:border-nobel-gold/50 transition-colors">
            <Icon size={24} />
        </div>
        <h4 className="font-serif font-bold text-xl text-[#0A0B0F] dark:text-white tracking-tight">{title}</h4>
        <p className="text-sm text-stone-500 dark:text-stone-400 leading-relaxed font-medium">{desc}</p>
    </div>
);

export const AboutPageView: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-24 animate-fade-in pb-32">
      
      {/* 1. Mission Narrative */}
      <div className="text-center space-y-8 py-16 relative overflow-hidden">
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-nobel-gold/5 blur-[120px] pointer-events-none rounded-full" />
         <div className="w-24 h-24 mx-auto relative group">
            <div className="absolute inset-0 bg-nobel-gold/20 blur-2xl group-hover:bg-nobel-gold/30 transition-all duration-700 rounded-full" />
            <div className="relative w-full h-full flex items-center justify-center p-5 rounded-[32px] bg-white dark:bg-stone-900 border border-stone-200 dark:border-white/10 shadow-2xl transition-transform duration-500 group-hover:scale-105">
                <GhostBrandIcon className="w-full h-full" />
            </div>
         </div>
         <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="font-serif text-5xl md:text-7xl text-[#0A0B0F] dark:text-white font-bold tracking-tighter leading-none">
                Engineering <br/><span className="italic text-stone-400 dark:text-stone-500 font-light">Financial Silence.</span>
            </h2>
            <p className="text-xl md:text-2xl text-stone-500 dark:text-stone-400 font-light leading-relaxed px-4">
                GhostPay is a declaration of independence. We are building the sovereign financial layer for an era that demands privacy as a default, not a feature.
            </p>
         </div>
      </div>

      {/* 2. Core Values */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 border-t border-stone-100 dark:border-white/5 pt-20">
          <ValueCard 
            icon={EyeOff}
            title="Absolute Privacy"
            desc="Financial metadata is decoupled from your physical identity through zero-knowledge logic and stealth routing. Silence is our global standard."
          />
          <ValueCard 
            icon={Shield}
            title="Radical Sovereignty"
            desc="Non-custodial by design. We provide high-performance rails, but you hold the keys to the vault. Always."
          />
          <ValueCard 
            icon={Zap}
            title="Atomic Velocity"
            desc="Legacy banking takes days; GhostPay takes milliseconds. Experience retail commerce at the speed of light on Solana and Sui."
          />
      </div>

      {/* 3. Technical Architecture */}
      <div className="space-y-12">
          <div className="border-t border-stone-100 dark:border-white/5 pt-20">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
                  <div className="space-y-1">
                      <h3 className="font-serif text-4xl font-bold text-[#0A0B0F] dark:text-white tracking-tight">GhostOS Infrastructure</h3>
                      <p className="text-sm text-stone-500 uppercase tracking-[0.2em] font-bold">Modular Full-Stack Architecture</p>
                  </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Modular Frontend */}
                  <Card className="p-10 border-stone-200 dark:border-white/5 bg-white dark:bg-[#15171C] group rounded-card">
                      <div className="flex items-center gap-5 mb-8">
                         <div className="p-3 bg-blue-500/10 rounded text-blue-500 shadow-inner group-hover:bg-blue-500 group-hover:text-white transition-all"><Layers size={28}/></div>
                         <div>
                            <h4 className="font-bold text-xl text-[#0A0B0F] dark:text-white">Reactive Interface Layer</h4>
                            <p className="text-[10px] text-stone-400 uppercase tracking-widest font-black">Modular Frontend</p>
                         </div>
                      </div>
                      <p className="text-stone-500 dark:text-stone-400 leading-relaxed mb-8">
                        Built with <strong>React 19</strong> and <strong>Tailwind CSS</strong>, our frontend is a collection of decoupled reactive modules. This enables sub-millisecond UI updates and seamless integration of complex 3D visualizations and real-time ledger data.
                      </p>
                      <div className="flex flex-wrap gap-2">
                          {['React 19', 'Tailwind v4', 'Framer Motion', 'Three.js', 'Concurrent UI'].map(t => (
                              <span key={t} className="px-3 py-1 rounded bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 text-[10px] font-mono font-bold text-stone-600 dark:text-stone-400">{t}</span>
                          ))}
                      </div>
                  </Card>

                  {/* High-Performance Backend */}
                  <Card className="p-10 border-stone-200 dark:border-white/5 bg-white dark:bg-[#15171C] group rounded-card">
                      <div className="flex items-center gap-5 mb-8">
                         <div className="p-3 bg-emerald-500/10 rounded text-emerald-500 shadow-inner group-hover:bg-emerald-500 group-hover:text-white transition-all"><Database size={28}/></div>
                         <div>
                            <h4 className="font-bold text-xl text-[#0A0B0F] dark:text-white">Secure Persistence Layer</h4>
                            <p className="text-[10px] text-stone-400 uppercase tracking-widest font-black">Isolated Backend Stack</p>
                         </div>
                      </div>
                      <p className="text-stone-500 dark:text-stone-400 leading-relaxed mb-8">
                        GhostPay isolates sensitive financial data through <strong>Neon Serverless Postgres</strong> for high-throughput transactional logging, while <strong>Supabase</strong> handles auth and real-time notification streams.
                      </p>
                      <div className="flex flex-wrap gap-2">
                          {['Neon DB', 'Supabase Auth', 'PostgreSQL', 'ZK-Proofs', 'RPC Indexing'].map(t => (
                              <span key={t} className="px-3 py-1 rounded bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 text-[10px] font-mono font-bold text-stone-600 dark:text-stone-400">{t}</span>
                          ))}
                      </div>
                  </Card>
              </div>
          </div>
      </div>

      {/* 4. Contact Us */}
      <div className="pt-20 border-t border-stone-100 dark:border-white/10">
          <div className="bg-[#0A0B0F] rounded-card p-10 md:p-20 text-white relative overflow-hidden shadow-2xl">
              <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white opacity-5 blur-[120px] rounded-full pointer-events-none -translate-y-1/2 translate-x-1/2"></div>
              
              <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-16">
                  <div className="max-w-xl space-y-8 text-center lg:text-left">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[10px] font-bold uppercase tracking-[0.4em] text-nobel-gold">
                          <Terminal size={12} /> Communication Endpoint
                      </div>
                      <h3 className="font-serif text-4xl md:text-6xl font-bold tracking-tight leading-tight">Connect with <br/><span className="text-stone-500 italic">Engineering.</span></h3>
                      <p className="text-gray-400 text-lg leading-relaxed font-light">
                        For institutional inquiries, terminal deployments, or developer support, reach out to our team via encrypted channel.
                      </p>
                      
                      <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                          <a href="mailto:ops@ghostpay.fun" className="group flex items-center justify-between gap-12 px-8 py-6 bg-white text-black rounded-card font-bold transition-all hover:bg-stone-200 shadow-xl">
                              <div className="flex items-center gap-4">
                                  <Mail size={24} className="text-stone-900"/>
                                  <div className="text-left leading-none">
                                      <div className="text-[10px] uppercase font-bold text-stone-500 mb-1">Encrypted Mailbox</div>
                                      <div className="font-mono text-lg">ops@ghostpay.fun</div>
                                  </div>
                              </div>
                              <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
                          </a>
                      </div>
                  </div>

                  <div className="shrink-0 flex flex-col items-center gap-6 group">
                      <div className="w-56 h-56 p-6 bg-white rounded-card flex items-center justify-center shadow-2xl rotate-3 group-hover:rotate-0 transition-transform duration-700 relative overflow-hidden">
                         <img src="https://raw.githubusercontent.com/ghost-protocol-labs/ghost-token-sui/refs/heads/main/assets/GHOST.svg" alt="Ghost Logo" className="w-full h-full opacity-10 group-hover:opacity-100 transition-opacity" />
                         <div className="absolute inset-0 flex items-center justify-center">
                            <Lock size={64} className="text-black/5" />
                         </div>
                      </div>
                      <div className="text-center">
                          <div className="text-[10px] text-stone-500 uppercase font-black tracking-[0.4em]">Sovereign Session Active</div>
                          <div className="flex items-center gap-2 mt-2 justify-center">
                             <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                             <span className="text-[10px] font-mono text-emerald-500 font-bold uppercase">TLS 1.3 | AES-256</span>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      </div>

      <div className="text-center pt-8">
          <p className="text-[10px] text-stone-400 uppercase tracking-[0.3em] font-bold">
              &copy; 2026 Ghost Network. Verified Infrastructure.
          </p>
      </div>
    </div>
  );
};
